<script lang="ts">
  import Button from '$components/elements/Button.svelte';
  import Cookie from '$components/elements/Cookie.svelte';
  import Icon from '$components/elements/Icon.svelte';
  import Toggle from '$components/elements/Toggle.svelte';
  import Flex from '$components/layout/Flex.svelte';
  import Grid from '$components/layout/Grid.svelte';
  import BasicLogo from '$components/logo/BasicLogo.svelte';
  import Desktop from '$components/utils/Desktop.svelte';
  import Mobile from '$components/utils/Mobile.svelte';
  import Text from '$components/utils/Text.svelte';
  import { analytics, marketing } from '$lib/gtm';
  import { showCookiesPopup } from '$lib/stores';
  import { Cookies, runAll, type PolicyConfig } from '@greenpanda/svelte-cookies';

  const domains = ['bible21.cz', '.bible21.cz', 'bible21.test', '.bible21.test'];
  const config: PolicyConfig[] = [
    {
      name: 'necessary',
      defaultValue: true,
      changable: false,
      cookies: []
    },
    {
      name: 'analytics',
      defaultValue: false,
      cookies: ['sc'],
      onAllow: () => {
        analytics.start();
      },
      onDeny: () => {
        analytics.stop();
      }
    },
    {
      name: 'marketing',
      defaultValue: false,
      cookies: [],
      onAllow: marketing.start,
      onDeny: marketing.stop
    }
  ];
  const titles = {
    necessary: 'Potřebné cookies',
    analytics: 'Analytické cookies',
    marketing: 'Marketingové cookies'
  };
  const getTitle = (name: string) => titles[name as keyof typeof titles];
  const close = () => ($showCookiesPopup = false);
  let mainSettings = true;
</script>

<Cookies {config} {domains} bind:showPopup={$showCookiesPopup}>
  <div class="cookies" slot="popup" let:state let:allowAll let:denyAll let:update let:saveAll>
    <div class="cookies__heading" class:cookies__heading--detailed={!mainSettings}>
      <Mobile>
        <BasicLogo />
      </Mobile>
      <Desktop>
        <Cookie />
      </Desktop>
      <h1><Text key="component.cookies.heading" /></h1>
    </div>
    <div class="cookies__content">
      {#if mainSettings}
        <p class="cookies__content__text">
          <Text key="component.cookies.text" />
        </p>
        <div class="cookies__content__buttons">
          <Button icon="cross" iconColor="#fff" iconSize={24} on:click={runAll(denyAll, close)}>
            <Text key="component.cookies.deny-button" />
          </Button>
          <Button
            color="#00BE22"
            icon="check"
            iconColor="#fff"
            iconSize={20}
            on:click={runAll(allowAll, close)}
          >
            <Text key="component.cookies.allow-button" />
          </Button>
        </div>
      {:else}
        <Grid
          breakpoints={{
            '0': {
              templateColumns: '1fr',
              width: '100%',
              gap: '24px 75px'
            },
            '720': {
              templateColumns: '314px 1fr',
              width: '100%',
              gap: '24px 75px'
            }
          }}
        >
          {#each Object.values(state) as conf}
            <Flex justifyContent="space-between" width="100%">
              <p class="cookies__content__title">{getTitle(conf.name)}</p>
              <Toggle
                active={conf.allowed}
                disabled={!conf.changable}
                onUpdate={(v) => {
                  update(conf.name, v);
                }}
              />
            </Flex>
          {/each}
          <p class="cookies__content__text cookies__content__text--detailed">
            <Text key="component.cookies.text" />
          </p>
        </Grid>
        <div class="cookies__content__buttons">
          <Button
            color="#00BE22"
            icon="check"
            iconColor="#fff"
            iconSize={20}
            on:click={runAll(saveAll, close)}
          >
            Uložit nastavení
          </Button>
        </div>
      {/if}
    </div>
    <div
      class="cookies__settings"
      on:click={() => (mainSettings = !mainSettings)}
      on:keypress={() => (mainSettings = !mainSettings)}
    >
      {#if mainSettings}
        <Icon icon="gear" size={18} />
        <Text key="component.cookies.more-options" />
      {:else}
        Obecná nastavení
      {/if}
    </div>
  </div>
</Cookies>

<style lang="scss">
  .cookies {
    width: 100%;
    position: fixed;
    bottom: 0;
    z-index: 1;
    background-color: $color-medium-gray;
    border-radius: 20px 20px 0px 0px;
    padding: 50px 75px 72px 75px;
    display: flex;
    flex-direction: column;
    gap: 30px;
    max-width: 1280px;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: 0px 6px 24px 0px rgba($color-dark-gray, 0.1);
    z-index: 1000;
    max-height: 100dvh;
    overflow-x: hidden;
    overflow-y: scroll;
    &__heading {
      display: flex;
      align-items: center;
      gap: 36px;
      h1 {
        font-weight: 800;
        font-size: 36px;
        line-height: 50px;
      }
      &--detailed {
        margin-bottom: 32px;
        h1 {
          position: relative;
          &::after {
            content: 'podrobnější nastavení';
            font-size: 24px;
            line-height: 37px;
            position: absolute;
            top: 95%;
            left: 0;
            color: $color-caramel;
            white-space: nowrap;
            letter-spacing: 1px;
          }
        }
      }
    }
    &__content {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 30px;
      &__text {
        width: 70%;
        line-height: 31px;
        &--detailed {
          grid-column: 2;
          grid-row: 1 / span 3;
          width: 100%;
        }
      }
      &__title {
        font-weight: 700;
        font-size: 16px;
        line-height: 30px;
      }
      &__buttons {
        display: flex;
        gap: 24px;
        align-self: center;
      }
    }
    &__settings {
      align-self: center;
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
    }
  }

  @include mobile {
    .cookies {
      border-radius: 0;
      padding: 16px 32px;
      gap: 20px;
      height: 100vh;
      &__content {
        height: 100%;
        &__text {
          width: 100%;
          &--detailed {
            grid-column: 1;
            grid-row: 1 / span 1;
          }
        }
        &__buttons {
          margin-top: auto;
          flex-direction: column;
          margin-bottom: 0;
        }
      }
      &__heading {
        gap: 12px;
        h1 {
          font-size: 28px;
        }
        &--detailed {
          margin-bottom: 32px;
          h1 {
            position: relative;
            &::after {
              content: 'podrobnější nastavení';
              font-size: 18px;
              line-height: 22px;
              position: absolute;
              top: 95%;
              left: 0;
              color: $color-caramel;
              white-space: nowrap;
              letter-spacing: 1px;
            }
          }
        }
      }
    }
  }
</style>
